/**
 * Faixas de prazo, usadas pela lista de tarefas e pela matriz.
 *
 * Existe como modulo proprio porque as duas telas precisam da MESMA nocao de
 * "amanha" e "proximos 7 dias". Duas copias divergiriam na primeira vez que
 * alguem ajustasse uma borda, e o sintoma seria um card em "amanha" na lista e
 * em "mais tarde" na matriz — o tipo de incoerencia que faz o usuario deixar de
 * confiar no agrupamento.
 *
 * Fica em `apps/web` e nao em `@cleartask/shared` porque e regra de
 * APRESENTACAO: o servidor nunca precisa saber em que caixa visual um card cai.
 * O criterio de `shared` e "servidor e cliente precisam calcular igual".
 *
 * Todas as bordas saem de `datetime.ts`, no fuso do usuario. Comparar contra
 * `Date.now()` cru erraria o dia para quem nao esta no fuso do dispositivo, e a
 * §4.4 proibe assumir horario de Brasilia.
 */

import {
  addDaysInZone,
  endOfDayInZone,
  startOfDayInZone,
  type Task,
} from '@cleartask/shared';

/** Faixa de prazo de um card, da mais urgente para a menos. */
export type DueHorizon = 'atraso' | 'hoje' | 'amanha' | 'proximos7' | 'maisTarde' | 'semPrazo';

/** Ordem de exibicao. `semPrazo` vem por ultimo: sem prazo nao e urgente. */
export const HORIZON_ORDER: DueHorizon[] = [
  'atraso',
  'hoje',
  'amanha',
  'proximos7',
  'maisTarde',
  'semPrazo',
];

export const HORIZON_LABEL_PT: Record<DueHorizon, string> = {
  atraso: 'Em atraso',
  hoje: 'Hoje',
  amanha: 'Amanhã',
  proximos7: 'Próximos 7 dias',
  maisTarde: 'Mais tarde',
  semPrazo: 'Sem prazo',
};

/**
 * Em que faixa uma tarefa cai.
 *
 * As bordas sao de DIA CIVIL, nao de "24 horas a partir de agora": uma tarefa
 * que vence hoje as 23h esta em "hoje" mesmo faltando 40 minutos, e uma que
 * vence amanha as 8h esta em "amanha" mesmo faltando 9 horas. Contar horas
 * corridas colocaria as duas na mesma caixa e contrariaria o que a pessoa le no
 * proprio card, que mostra a data.
 */
export function dueHorizon(dueAt: number | null, now: number, timeZone: string): DueHorizon {
  if (dueAt === null) return 'semPrazo';

  const inicioDeHoje = startOfDayInZone(timeZone, now);
  if (dueAt < inicioDeHoje) return 'atraso';

  const fimDeHoje = endOfDayInZone(timeZone, now);
  if (dueAt <= fimDeHoje) return 'hoje';

  const fimDeAmanha = endOfDayInZone(timeZone, addDaysInZone(timeZone, now, 1));
  if (dueAt <= fimDeAmanha) return 'amanha';

  // "Proximos 7 dias" conta a partir de HOJE, entao a borda e o fim do dia +7.
  // Amanha ja saiu na faixa anterior; esta cobre de +2 a +7.
  const fimDaSemana = endOfDayInZone(timeZone, addDaysInZone(timeZone, now, 7));
  if (dueAt <= fimDaSemana) return 'proximos7';

  return 'maisTarde';
}

export interface HorizonGroup {
  horizon: DueHorizon;
  label: string;
  tasks: Task[];
}

/**
 * Agrupa preservando a ordem de entrada dentro de cada faixa.
 *
 * Faixa vazia nao entra no resultado: um cabecalho "Em atraso" sobre nenhum
 * card afirmaria que existe algo atrasado.
 */
export function groupByDueHorizon(
  tasks: Task[],
  now: number,
  timeZone: string,
): HorizonGroup[] {
  const balde = new Map<DueHorizon, Task[]>();

  for (const task of tasks) {
    const horizon = dueHorizon(task.dueAt, now, timeZone);
    const atual = balde.get(horizon);
    if (atual) atual.push(task);
    else balde.set(horizon, [task]);
  }

  return HORIZON_ORDER.flatMap((horizon) => {
    const tarefas = balde.get(horizon);
    if (!tarefas || tarefas.length === 0) return [];

    return [{ horizon, label: HORIZON_LABEL_PT[horizon], tasks: tarefas }];
  });
}
